// ============================================================
// AbuseNoLimitBlock.tsx — the Claim card's "no time limit" block for child
// sexual abuse (Limitation Act s.11ZA; DB batch 2 / B2b).
//   • OFFERED only while the SAVED claim is ABUSE, abuse type Sexual, and the
//     DOB shows the client under 18 at the accident date
//     (utils/limitationRules abuseConfirmOffered) — or while one is stored,
//     so it can always be removed.
//   • Confirming needs all three statements ticked; the server checks the
//     same and audit-logs it (POST /api/cases/:id/abuse-no-limit). Removing
//     asks once more, then DELETEs (audit-logged too).
//   • Confirmed → "No time limit (Limitation Act s.11ZA). Confirmed by [name]
//     on [date]. The defendant can still argue a fair trial isn't possible."
//   • (B2b follow-up) A stored confirmation that no longer FITS (checked now —
//     e.g. the DOB was changed on the client page) counts as unconfirmed:
//     "Confirmation no longer applies. Check and confirm again." — with Remove.
//     (If the facts change back so it fits again, the stored confirmation
//     counts again.)
//   • (B2b follow-up) Only the case's fee earner (owner / handling team) or an
//     admin may confirm or remove it (the server refuses others, 403); others
//     see the block with its controls disabled and say who can.
// Its own action, not part of the card's draft / Save.
// ============================================================
import ActionBox from '../shared/ActionBox'
import React, { useState } from 'react'
import { BTN_SEC, BTN_PRI, SM } from '../clientInfo/ClientInfoSections'
import { fmtDay } from '../case/caseUi'
import { ABUSE_CONFIRM_FORBIDDEN, claimOfRaw } from '../../utils/caseClaim'
import { LIMITATION_NOTES, NO_TIME_LIMIT, NO_TIME_LIMIT_CAVEAT } from '../../utils/limitationRules'
import { applyRemoteCaseEdit, flushAutoSave } from '../../hooks/useAutoSave'
import { refusedField } from '../../hooks/fieldRefusals'
import { confirmAbuseNoLimitApi, unconfirmAbuseNoLimitApi } from '../../api/casevoApi'
import type { LimitationOverrideSetBy, RawCase } from '../../types/case.types'

export const ABUSE_STATEMENTS = [
  ['sexualAbuse', 'The claim is for sexual abuse.'],
  ['underEighteen', 'The client was under 18 when it happened.'],
  ['neverSettled', 'The claim has never been settled or decided by a court.'],
] as const
type Key = typeof ABUSE_STATEMENTS[number][0]
const NONE: Record<Key, boolean> = { sexualAbuse: false, underEighteen: false, neverSettled: false }
export const CONFIRM_FAILED = 'Couldn’t save. Check your connection and try again — nothing was changed.'
export const confirmedLine = (by: LimitationOverrideSetBy | null | undefined, at: string | null): string =>
  `${NO_TIME_LIMIT} Confirmed by ${by?.name ?? 'a colleague'} on ${fmtDay((by?.at ?? at ?? '').slice(0, 10))}. ${NO_TIME_LIMIT_CAVEAT}`

const ONLY = <p className="mt-[8px] mb-0 text-[12.5px] text-casevo-text-soft" role="note">{ABUSE_CONFIRM_FORBIDDEN}</p>

const AbuseNoLimitBlock: React.FC<{
  caseId: string
  confirmedAt: string | null
  confirmedBy: LimitationOverrideSetBy | null | undefined
  // The stored confirmation still fits the case NOW (noTimeLimitApplies).
  applies: boolean
  // The viewer is the fee earner (owner / team) or an admin.
  canConfirm: boolean
}> = ({ caseId, confirmedAt, confirmedBy, applies, canConfirm }) => {
  const [ticks, setTicks] = useState<Record<Key, boolean>>(NONE)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [askRemove, setAskRemove] = useState(false)
  const all = ABUSE_STATEMENTS.every(([k]) => ticks[k])
  const locked = busy || !canConfirm

  const run = async (fn: () => Promise<RawCase>): Promise<void> => {
    if (busy || !canConfirm) return
    setBusy(true); setError(null)
    try {
      await flushAutoSave()
      const row = await fn()
      applyRemoteCaseEdit({ claim: claimOfRaw(row), abuseNoLimitConfirmedBy: row.abuseNoLimitConfirmedBy ?? null }, row.updatedAt)
      setTicks(NONE); setAskRemove(false)
    } catch (err) {
      setError(refusedField(err)?.message ?? (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? CONFIRM_FAILED)
    } finally {
      setBusy(false)
    }
  }

  const removeRow = (
    <div className="mt-[10px] flex items-center gap-2 flex-wrap">
      {askRemove ? (
        <>
          <span className="text-[12.5px] text-casevo-text-soft mr-1">Remove the confirmation? The usual limitation date will show again.</span>
          <button type="button" className={`${BTN_SEC} ${SM}`} onClick={() => setAskRemove(false)} disabled={busy}>Keep it</button>
          <button type="button" className={`${BTN_PRI} ${SM}`} onClick={() => { void run(() => unconfirmAbuseNoLimitApi(caseId)) }} disabled={locked}>{busy ? 'Removing…' : 'Yes, remove'}</button>
        </>
      ) : (
        <button type="button" className={`${BTN_SEC} ${SM}`} onClick={() => setAskRemove(true)} disabled={locked} aria-disabled={locked}>Remove confirmation</button>
      )}
    </div>
  )
  const confirmForm = (
    <>
      <fieldset className="m-0 p-0 border-0 space-y-[6px]" disabled={locked}>
        <legend className="sr-only">Confirm all three</legend>
        {ABUSE_STATEMENTS.map(([k, label]) => (
          <label key={k} className={`flex items-start gap-[9px] text-[13px] text-casevo-forest select-none ${locked ? 'opacity-60' : 'cursor-pointer'}`}>
            <input type="checkbox" className="h-[17px] w-[17px] mt-px accent-casevo-brand shrink-0" checked={ticks[k]} disabled={locked}
              onChange={(e) => { setTicks((t) => ({ ...t, [k]: e.target.checked })); setError(null) }} />
            {label}
          </label>
        ))}
      </fieldset>
      <div className="mt-[10px] flex items-center gap-2 flex-wrap">
        <button type="button" className={`${BTN_PRI} ${SM}`} disabled={!all || locked} aria-disabled={!all || locked}
          onClick={() => { void run(() => confirmAbuseNoLimitApi(caseId, ticks)) }}>
          {busy ? 'Confirming…' : 'Confirm no time limit'}
        </button>
        {canConfirm && !all && <span className="text-[12px] text-casevo-text-soft">Tick all three to confirm.</span>}
      </div>
    </>
  )
  const errorLine = error && <p role="alert" className="mt-2 mb-0 text-[12.5px] text-casevo-danger">{error}</p>

  // Confirmed and it still fits.
  if (confirmedAt && applies) {
    return (
      // (Audit fix batch 2) The shared note shell: green = confirmed.
      <ActionBox ariaLabel="No time limit" tone="done" className="mt-[14px]" message={confirmedLine(confirmedBy, confirmedAt)}>
        {removeRow}
        {!canConfirm && ONLY}
        {errorLine}
      </ActionBox>
    )
  }
  // Stored, but no longer fits → counts as unconfirmed.
  if (confirmedAt) {
    return (
      <ActionBox ariaLabel="No time limit confirmation no longer applies" tone="warn" className="mt-[14px]" title={LIMITATION_NOTES.abuseConfirmStale}
        message={`It was confirmed on ${fmtDay((confirmedBy?.at ?? confirmedAt).slice(0, 10))}, but the claim type, type of abuse or dates no longer show a child sexual abuse claim. The usual limitation date applies until it’s confirmed again.`}>
        {removeRow}
        {!canConfirm && ONLY}
        {errorLine}
      </ActionBox>
    )
  }
  return (
    // The three ticks sit in a plain white section beneath the facts.
    <ActionBox ariaLabel="Confirm no time limit" tone="info" className="mt-[14px]" title="No time limit? (child sexual abuse)"
      message="From 29 June 2026, claims for sexual abuse of a child may have no time limit (Limitation Act s.11ZA). Confirm only if all three are true."
      form={<>{confirmForm}{!canConfirm && ONLY}{errorLine}</>} />
  )
}

export default AbuseNoLimitBlock
